import React from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Stack,
  IconButton,
  TextField,
  Button,
  Divider,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Logo from "../../assets/images/MC3.png";
import { Link } from "react-router-dom";

const quickLinks = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about-us" },
  { label: "Our Services", path: "/our-services" },
  { label: "Expertise", path: "/expertize" },
  { label: "Support", path: "/support" },
  { label: "Careers", path: "/careers" },
];

const policyLinks = [
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms of Service", path: "/terms-of-service" },
];

const socialLinks = [
  { label: "LinkedIn", icon: LinkedInIcon, href: "https://www.linkedin.com/company/mc3-smart-tech" },
  { label: "Twitter", icon: TwitterIcon, href: "https://twitter.com/mc3smarttech" },
  { label: "YouTube", icon: YouTubeIcon, href: "https://www.youtube.com/@mc3smarttech" },
];

const Footer = () => {
  return (
    <Box
      component="footer"
      sx={{
        background: "linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e293b 100%)",
        color: "#f8fafc",
        pt: { xs: 8, md: 10 },
        pb: 4,
        mt: 10,
      }}
    >
      <Container>
        <Grid container spacing={6}>
          <Grid item xs={12} md={4}>
            <Box component={Link} to="/" sx={{ display: "inline-flex" }}>
              <img src={Logo} alt="MC3 Smart Tech" style={{ height: 60 }} />
            </Box>
            <Typography variant="body2" sx={{ mt: 3, color: "rgba(248,250,252,0.8)", maxWidth: 360 }}>
              MC3 Smart Tech is a UK-founded studio blending strategy, design, engineering, and cybersecurity to build
              products people rely on every day.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.label}
                  color="inherit"
                  size="large"
                  aria-label={`Visit our ${social.label} page`}
                  component="a"
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ border: "1px solid rgba(248,250,252,0.2)" }}
                >
                  <social.icon />
                </IconButton>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Quick Links
            </Typography>
            <Grid container spacing={1.5}>
              {quickLinks.map((link) => (
                <Grid item xs={6} key={link.path}>
                  <Box
                    component={Link}
                    to={link.path}
                    style={{ textDecoration: "none", color: "rgba(248,250,252,0.85)", fontWeight: 500 }}
                  >
                    {link.label}
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 1 }}>
              Policies
            </Typography>
            <Stack spacing={1}>
              {policyLinks.map((link) => (
                <Box
                  key={link.path}
                  component={Link}
                  to={link.path}
                  style={{ textDecoration: "none", color: "rgba(248,250,252,0.85)" }}
                >
                  {link.label}
                </Box>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Contact
            </Typography>
            <Stack spacing={1.5} component="address" sx={{ fontStyle: "normal", color: "rgba(248,250,252,0.85)" }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <EmailIcon fontSize="small" />
                <a href="mailto:contact@mc3smarttech.com" style={{ color: "inherit", textDecoration: "none" }}>
                  contact@mc3smarttech.com
                </a>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <PhoneIcon fontSize="small" />
                <a href="tel:+15183944014" style={{ color: "inherit", textDecoration: "none" }}>
                  +1 518 394 4014
                </a>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <LocationOnIcon fontSize="small" />
                <Typography variant="body2">
                  New Forest Enterprise Centre, Unit 36b, Chapel Lane, Totton, Southampton, SO40 9LA
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="subtitle2" sx={{ mt: 4, mb: 1, color: "rgba(248,250,252,0.85)" }}>
              Receive launch notes & playbooks
            </Typography>
            <Stack direction="row" component="form" spacing={1}>
              <TextField
                type="email"
                size="small"
                placeholder="Work email"
                variant="outlined"
                fullWidth
                InputProps={{
                  sx: {
                    borderRadius: 999,
                    backgroundColor: "#fff",
                  },
                }}
              />
              <Button
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  borderRadius: 999,
                  background: "linear-gradient(120deg, #f97316, #fbbf24)",
                  color: "#0f172a",
                  fontWeight: 700,
                  px: 3,
                }}
              >
                Join
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: "rgba(248,250,252,0.1)" }} />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Typography variant="body2" color="rgba(248,250,252,0.7)">
            © {new Date().getFullYear()} MC3 Smart Tech. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2}>
            {policyLinks.map((link) => (
              <Box
                key={`policy-bottom-${link.path}`}
                component={Link}
                to={link.path}
                style={{ color: "rgba(248,250,252,0.8)", textDecoration: "none", fontSize: "0.9rem" }}
              >
                {link.label}
              </Box>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
